import React from 'react'

export enum StepOption {
  BRAND = 'BRAND',
  MODEL = 'MODEL',
  TYPE = 'TYPE',
  CONFIRM = 'CONFIRM',
  DONE = 'DONE'
}

export const stepArrangement = [StepOption.BRAND, StepOption.MODEL, StepOption.TYPE, StepOption.CONFIRM, StepOption.DONE]

const initialState = {
  currentStep: stepArrangement[0],
  brand: undefined,
  model: undefined,
  type: undefined,
  vehicleUUID: undefined,
  typeSelectMode: false
}

function reducer(state: UnkownVehicleStepperState, action: ActionTypes) {
  switch (action.type) {
    case UnkownVehicleSetepperAction.SET_BRAND: {
      return { ...state, brand: action.payload, currentStep: stepArrangement[1], model: undefined, type: undefined }
    }
    case UnkownVehicleSetepperAction.SET_MODEL: {
      return { ...state, model: action.payload, currentStep: stepArrangement[2], type: undefined }
    }
    case UnkownVehicleSetepperAction.SET_TYPE: {
      return { ...state, type: action.payload, currentStep: stepArrangement[3] }
    }
    case UnkownVehicleSetepperAction.SET_CONFIRM: {
      return { ...state, currentStep: stepArrangement[4], vehicleUUID: action.payload }
    }
    case UnkownVehicleSetepperAction.SET_STEP: {
      return { ...state, currentStep: action.payload }
    }
    case UnkownVehicleSetepperAction.RESET: {
      return { ...initialState }
    }
  }
}

export const SelectionContext = React.createContext<ContextExpose>({} as ContextExpose)
export const SelectionContextProvider: React.FC<{ typeSelectMode?: boolean }> = ({ children, typeSelectMode }) => {
  const [{ currentStep, brand, model, type, vehicleUUID }, dispatch] = React.useReducer<UnkownVehicleStepperReducer>(reducer, initialState)

  const setBrand = (payload: SelectionPayload) => {
    dispatch({ type: UnkownVehicleSetepperAction.SET_BRAND, payload })
  }

  const setModel = (payload: SelectionPayload) => {
    dispatch({ type: UnkownVehicleSetepperAction.SET_MODEL, payload })
  }

  const setType = (payload: SelectionPayload) => {
    dispatch({ type: UnkownVehicleSetepperAction.SET_TYPE, payload })
  }

  const setStep = (payload: StepOption) => {
    dispatch({ type: UnkownVehicleSetepperAction.SET_STEP, payload })
  }

  const setConfirm = (payload: string) => {
    dispatch({ type: UnkownVehicleSetepperAction.SET_CONFIRM, payload })
  }

  const reset = () => {
    dispatch({ type: UnkownVehicleSetepperAction.RESET })
  }

  const activeIndex = stepArrangement.findIndex(step => step === currentStep)

  const value = React.useMemo(
    () => ({
      currentStep,
      brand,
      setBrand,
      model,
      setModel,
      type,
      setType,
      reset,
      setStep,
      setConfirm,
      activeIndex,
      vehicleUUID,
      typeSelectMode
    }),
    [currentStep, activeIndex, brand, setBrand, model, setModel, type, setType, reset, setStep, setConfirm, vehicleUUID, typeSelectMode]
  )

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
}

/**
 * useSelectionContext contains form values for unkown vehicle selection.
 * @returns
 */
export const useSelectionContext = () => {
  const context = React.useContext(SelectionContext)
  if (context === undefined) {
    throw new Error('useSelectionContext must be used within a SelectionContextProvider')
  }
  return context
}

interface ContextExpose extends UnkownVehicleStepperState {
  setBrand: (payload: SelectionPayload) => void
  setModel: (payload: SelectionPayload) => void
  setType: (payload: SelectionPayload) => void
  setStep: (payload: StepOption) => void
  setConfirm: (payload: string) => void
  reset: () => void
  activeIndex: number
  typeSelectMode?: boolean
}

export interface UnkownVehicleStepperState {
  currentStep: StepOption
  vehicleUUID?: string
  brand: SelectionPayload | undefined
  model: SelectionPayload | undefined
  type: SelectionPayload | undefined
}

export enum UnkownVehicleSetepperAction {
  RESET = 'RESET',
  SET_STEP = 'SET_STEP',
  SET_BRAND = 'SET_BRAND',
  SET_MODEL = 'SET_MODEL',
  SET_TYPE = 'SET_TYPE',
  SET_CONFIRM = 'SET_CONFIRM'
}
interface SelectionPayload {
  key: string
  name: string
  value: any
}
interface SetStep {
  type: UnkownVehicleSetepperAction.SET_STEP
  payload: StepOption
}

interface SetBrandAction {
  type: UnkownVehicleSetepperAction.SET_BRAND
  payload: SelectionPayload
}

interface SetModelAction {
  type: UnkownVehicleSetepperAction.SET_MODEL
  payload: SelectionPayload
}

interface SetTypeAction {
  type: UnkownVehicleSetepperAction.SET_TYPE
  payload: SelectionPayload
}

interface SetConfirmAction {
  type: UnkownVehicleSetepperAction.SET_CONFIRM
  payload: string
}
interface ResetAction {
  type: UnkownVehicleSetepperAction.RESET
}

export type ActionTypes = SetBrandAction | SetModelAction | SetTypeAction | ResetAction | SetStep | SetConfirmAction
export type UnkownVehicleStepperReducer = (state: UnkownVehicleStepperState, action: ActionTypes) => UnkownVehicleStepperState
